'use client';

import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ActivityBanner from '../components/ActivityBanner';
import FaqAccordion from '../components/FaqAccordion';
import BottomBanner from './components/BottomBanner';
import FlashSaleArea from './components/FlashSaleArea';
import Promotion from './components/Promotion';
import YouMightLike from './components/YouMightLike';
import { useFlashSaleTracking } from './hooks/useFlashSaleTracking';
import { FlashSaleClientProps, FlashSaleData } from './types';

export default function FlashSaleClientPage({
  serverData,
  deviceType,
  remainingSeconds = 0,
  restrictPurchase = 2,
  flashSaleList = [],
  promotionList = [],
  promotionLimit = 36,
}: FlashSaleClientProps) {
  const [pageData] = useState<FlashSaleData>(serverData);
  const [allRecommendListLoaded, setAllRecommendListLoaded] = useState(false);

  // Flash Sale相关状态
  const [currentRestrictPurchase, setCurrentRestrictPurchase] = useState(restrictPurchase);
  const [inAppH5, setInAppH5] = useState([EPlatform.IOS, EPlatform.ANDROID].includes(deviceType));
  const [isAppCommunicationReady, setIsAppCommunicationReady] = useState(!inAppH5); // App通信完成状态，非App环境默认为true
  const isAppCommunicationReadyRef = useRef(isAppCommunicationReady); // 用于超时检查的ref

  // 同步ref和state
  useEffect(() => {
    isAppCommunicationReadyRef.current = isAppCommunicationReady;
  }, [isAppCommunicationReady]);

  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const isWap = deviceType !== EPlatform.WEB;

  // 缓存Banner数据和aspectRatio
  const bannerData = useMemo(() => pageData?.banner, [pageData?.banner]);
  const bannerAspectRatio = useMemo(
    () => pageData?.banner?.aspectRatio,
    [pageData?.banner?.aspectRatio],
  );

  // 检测是否在APP内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInApp = userAgent.includes('nextmar-xms-app');
      setInAppH5(isInApp);
      // 如果检测到是App环境，重置通信完成状态
      if (isInApp) {
        setIsAppCommunicationReady(false);
      }
    }
  }, []);

  // 初始化埋点追踪
  const { trackPageView, trackPageLeave } = useFlashSaleTracking();

  // App通信初始化
  useEffect(() => {
    logger.log('flash sale 1 inAppH5:', inAppH5);
    if (inAppH5) {
      // 处理App通信逻辑
      const handleAppCommunication = async () => {
        try {
          // 清空当前用户信息
          setToken('');
          setUserInfo({});
          logger.log('flash sale 2 setToken and setUserInfo');

          // 注册token回调通信
          appCommunicationManager.registerCallback(
            EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
            async (_token) => {
              logger.log('flash sale 2 App登录token:', _token);
              if (_token !== 'ClearToken') {
                setToken(_token);
                // 获取用户信息
                await refreshAccountInfo();
                setIsAppCommunicationReady(true);
              } else {
                // 清除token时也标记为通信完成（游客状态）
                setIsAppCommunicationReady(true);
              }
            },
            {},
            'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
          );
        } catch (e) {
          logger.error('App通信初始化失败:', e);
          // 初始化失败时也标记为完成，避免页面一直不加载
          setIsAppCommunicationReady(true);
        }
      };

      handleAppCommunication();

      // APP内时监听appView和appLeave事件
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);

      // 添加超时机制，防止App通信失败导致页面一直加载
      const timeout = setTimeout(() => {
        if (!isAppCommunicationReadyRef.current) {
          logger.warn('App通信超时，将以游客状态继续渲染页面');
          setIsAppCommunicationReady(true);
        }
      }, 5000); // 5秒超时

      return () => {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
        clearTimeout(timeout);
      };
    }
  }, [inAppH5]);

  // 组件卸载时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 页面加载完成后触发页面浏览埋点，组件卸载时触发离开埋点
  useEffect(() => {
    if (pageData) {
      logger.log('flash sale 3 pageData:');
      // 只在WAP端：进入页面时隐藏底部导航，等推荐商品加载完成后再显示
      if (isWap) {
        setTimeout(() => {
          setIsShowFooterNav(false);
        }, 50);
      }
      trackPageView();
    }
    return () => {
      trackPageLeave();
    };
  }, []);

  // 推荐商品加载完成回调
  const handleRecommendLoadComplete = useCallback(
    (isComplete: boolean) => {
      setAllRecommendListLoaded(isComplete);
      // 只在WAP端：推荐商品加载完成后才显示底部导航
      if (isComplete && isWap) {
        setIsShowFooterNav(true);
      }
    },
    [isWap],
  );

  if (!pageData) {
    return null;
  }

  return (
    <div className="bg-[#F4F4F4]">
      {/* Banner区域 */}
      {bannerData && (
        <ActivityBanner
          data={{
            alt: bannerData.image_alt,
            image: bannerData.image,
            type: bannerData.type,
            jump_content: bannerData.jump_content,
          }}
          aspectRatio={bannerAspectRatio}
        />
      )}

      {/* Flash Sale限购区域 - App内嵌时需要等待通信完成 */}
      {pageData.flash_sale && isAppCommunicationReady && (
        <FlashSaleArea
          listData={flashSaleList}
          flashSaleData={pageData.flash_sale}
          remainingSeconds={remainingSeconds}
          restrictPurchase={currentRestrictPurchase}
          onRestrictPurchaseChange={(newRestrictPurchase) => {
            setCurrentRestrictPurchase(newRestrictPurchase);
          }}
        />
      )}

      {/* Promotion促销区域 */}
      {promotionList.length > 0 && pageData.promotion && (
        <Promotion
          groupId={pageData.promotion.group_id}
          promotionData={pageData.promotion}
          promotionList={promotionList}
          restrictPurchase={currentRestrictPurchase}
          deviceType={deviceType}
          limit={promotionLimit}
        />
      )}

      {/* BottomBanner底部横幅区域 */}
      {pageData.bottom_banner && pageData.bottom_banner.length > 0 && (
        <BottomBanner bottomBannerData={pageData.bottom_banner} deviceType={deviceType} />
      )}

      {/* YouMightLike猜你喜欢推荐商品 */}
      <YouMightLike
        deviceType={deviceType}
        onLoadComplete={handleRecommendLoadComplete}
        isInApp={inAppH5}
      />

      {/* FAQ区域 - 只在推荐商品加载完成后显示 */}
      {allRecommendListLoaded && pageData.faq && (
        <div className="faq-section">
          <FaqAccordion
            backgroundColor="bg-[#F4F4F4]"
            faqData={{ ...pageData.faq, title: pageData.faq.title }}
          />
        </div>
      )}
    </div>
  );
}
